import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { TodaysMeals } from "@/components/today/TodaysMeals";
import { EmptyState } from "@/components/system";
import { MacrosRing } from "@/components/health/MacrosRing";
import { EnergyBalanceChart } from "@/components/health/EnergyBalanceChart";
import { TopFoodsThisMonth } from "@/components/health/TopFoodsThisMonth";

/**
 * /health/nutrition — sub-area home (SPEC 3.3.1). v1 stack:
 *  - Reuses TodaysMeals as the "Today" tab
 *  - Lists the active Supplement stack (recovery sub-area links the rules)
 *  - Defers Macros ring + DayPlan slot list + Recipes/Pantry/Shopping tabs to BW9
 */
export function HealthNutrition() {
  return (
    <article aria-labelledby="nutrition-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        <Link to="/health" style={{ color: "var(--fg-3)" }}>
          Health
        </Link>{" "}
        · Nutrition
      </p>
      <h1
        id="nutrition-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-8)",
        }}
      >
        How you ate.
      </h1>

      <div style={{ display: "grid", gap: "var(--space-6)" }}>
        <MacrosRing />
        <EnergyBalanceChart />
        <TodaysMeals />
        <TopFoodsThisMonth />
        <SupplementStack />
        <PantryShortcut />
      </div>
    </article>
  );
}

function SupplementStack() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const q = useQuery({
    queryKey: ["nutrition_supplements", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("supplement")
        .select("id, name, dose, unit, schedule_frequency, tier, status")
        .eq("user_id", userId)
        .eq("status", "active")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <article
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        padding: "var(--space-4)",
      }}
    >
      <h3 style={{ margin: 0, marginBottom: "var(--space-3)", fontSize: "var(--type-body)" }}>
        Supplement stack
      </h3>
      {q.isLoading ? (
        <p style={{ color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>Loading…</p>
      ) : !q.data || q.data.length === 0 ? (
        <EmptyState
          headline="No active supplements."
          sub="Bootstrap your stack on first onboarding, or add via the Quick-log Supplement sheet."
        />
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 6 }}>
          {q.data.map((s) => (
            <li
              key={s.id}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                gap: "var(--space-3)",
                alignItems: "baseline",
                fontSize: "var(--type-body-s)",
                color: "var(--fg-2)",
                padding: "4px 0",
                borderBottom: "1px solid var(--hair)",
              }}
            >
              <span style={{ color: "var(--fg)" }}>{s.name}</span>
              <span className="mono" style={{ color: "var(--fg-3)" }}>
                {s.dose ? `${Number(s.dose)}${s.unit}` : ""}
              </span>
              <span style={{ color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
                {s.schedule_frequency?.replace(/_/g, " ") ?? ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function PantryShortcut() {
  return (
    <Link
      to="/pantry"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
        color: "var(--fg)",
        textDecoration: "none",
      }}
    >
      <span>Pantry</span>
      <span style={{ color: "var(--accent)" }}>Open →</span>
    </Link>
  );
}
